<template>
  <div class="Workspace">
    <header class="Workspace__header">
      <div
        class="Workspace__header-container d-flex justify-space-between align-center"
      >
        <div class="d-flex align-center">
          <p class="Workspace__header-title">
            <router-link
              to="/agents"
              tag="a"
              class="Workspace__header-title-link"
            >
              Agents
            </router-link>
            <v-icon small class="Workspace__header-title-icon">
              mdi-chevron-right
            </v-icon>
            <span class="Workspace__header-title-name">
              {{ workspace.name }}
            </span>
          </p>
        </div>
      </div>
    </header>

    <v-col v-if="!workspaceLoading">
      <div style="padding: 25px; padding-bottom: 0px; padding-top: 10px">
        <div class="Workspace__settings">
          <div class="section">
            <span style="font-size: 14pt; margin-bottom: 15px">
              {{ workspace.description }}
            </span>
            <div class="line"></div>
          </div>

          <div class="section">
            <div class="content">
              <div class="inline">
                <div class="inlineItem">
                  <v-row style="display: flex">
                    <v-col style="flex-grow: 1">
                      <v-textarea
                        :disabled="!canEdit()"
                        class="TextAreaSystemPrompt"
                        outlined
                        dense
                        :placeholder="'System Prompt'"
                        hide-details="auto"
                        novalidate
                        :rows="3"
                        v-model="systemPromptText"
                        label="System Prompt"
                      >
                      </v-textarea>
                    </v-col>
                  </v-row>
                </div>
                <div class="inlineItem">
                  <v-autocomplete
                    :disabled="!canEdit()"
                    style="width: 250px; border-radius: 15px"
                    outlined
                    dense
                    label="AI Model"
                    :items="models"
                    v-model="selectedModel"
                    item-text="label"
                    item-value="value"
                    @change="
                      () => {
                        updateWorkspaceInfo();
                      }
                    "
                  ></v-autocomplete>
                  <v-switch
                    v-if="isExtendedModeSupported"
                    label="Extended Mode"
                    v-model="workspace.modelThinking"
                    @change="
                      (val) => {
                        setWorkspace({
                          ...workspace,
                          modelThinking: val,
                        });
                        updateWorkspaceInfo();
                      }
                    "
                  >
                  </v-switch>
                </div>
              </div>
            </div>
          </div>

          <div class="section">
            <div class="content">
              <div class="inline">
                <div class="inlineItem">
                  <div class="maxTokens">
                    <span style="margin-left: 7px">
                      Number of latest included messages
                    </span>
                    <v-slider
                      v-model="historyLimit"
                      @change="
                        () => {
                          updateWorkspaceInfo();
                        }
                      "
                      :min="0"
                      :max="50"
                      :disabled="includeAllHistory || !canEdit()"
                    >
                      <template v-slot:append>
                        <v-text-field
                          @input="
                            () => {
                              updateWorkspaceInfo();
                            }
                          "
                          v-model="historyLimit"
                          density="compact"
                          style="width: 50px; margin-top: -20px"
                          type="number"
                          hide-details
                          single-line
                          max="50"
                          min="0"
                        ></v-text-field>
                      </template>
                    </v-slider>
                  </div>
                  <v-checkbox
                    v-if="isShAdmin() && canEdit()"
                    label="Include All"
                    v-model="includeAllHistory"
                    style="margin-top: -15px"
                    @change="
                      () => {
                        updateWorkspaceInfo();
                      }
                    "
                  >
                  </v-checkbox>
                </div>
                <div class="inlineItem">
                  <div class="maxTokens">
                    <span> Max Tokens </span>
                    <v-slider
                      @change="
                        () => {
                          updateWorkspaceInfo();
                        }
                      "
                      :disabled="!canEdit()"
                      v-model="maxTokens"
                      :min="1025"
                      :max="15000"
                    >
                      <template v-slot:append>
                        <span>
                          <v-text-field
                            v-model="workspace.maxTokens"
                            @input="
                              () => {
                                updateWorkspaceInfo();
                              }
                            "
                            density="compact"
                            style="width: 50px; margin-top: -20px"
                            type="number"
                            hide-details
                            single-line
                            max="15000"
                            min="1025"
                          ></v-text-field>
                        </span>
                      </template>
                    </v-slider>
                  </div>
                </div>
                <div
                  class="inlineItem"
                  v-if="workspace.modelThinking && isExtendedModeSupported"
                >
                  <div class="thinkingTokens">
                    <span> Thinking Tokens </span>
                    <v-slider
                      @change="
                        () => {
                          updateWorkspaceInfo();
                        }
                      "
                      :disabled="!canEdit()"
                      v-model="thinkingTokens"
                      :min="1024"
                      :max="15000"
                    >
                      <template v-slot:append>
                        <span>
                          <v-text-field
                            v-model="workspace.thinkingTokens"
                            @input="
                              () => {
                                updateWorkspaceInfo();
                              }
                            "
                            density="compact"
                            style="width: 50px; margin-top: -20px"
                            type="number"
                            hide-details
                            single-line
                            max="15000"
                            min="1024"
                          ></v-text-field>
                        </span>
                      </template>
                    </v-slider>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="section">
            <div class="title">
              Custom Actions
              <v-btn
                style=""
                v-if="canEdit()"
                icon
                @click.stop="isToolSelectDialog = true"
              >
                <v-icon small> mdi-pencil-outline </v-icon>
              </v-btn>
            </div>
            <div class="content">
              <div class="selectedToolsList">
                <div
                  class="selectedToolItem"
                  v-for="tool in workspace.tools"
                  :key="tool.id"
                >
                  {{ tool.name }}
                </div>
              </div>
            </div>
          </div>

          <div class="section">
            <div class="content">
              <general-actions />
            </div>
          </div>

          <div class="section" style="margin-bottom: 45px">
            <div class="title">Agent Knowledge</div>
            <div>
              <v-switch
                v-model="workspace.advancedKnowledgeBase"
                @change="() => updateWorkspaceInfo()"
                color="primary"
                label="Advanced Knowledge Base"
              ></v-switch>
            </div>
            <div class="content">
              <workspace-knowledge
                v-if="!workspace.advancedKnowledgeBase"
                :json-knowledge="workspace.jsonKnowledge"
                @jsonKnowledgeChanged="
                  (data) => {
                    workspace.jsonKnowledge = data;
                    updateWorkspaceInfo();
                  }
                "
              />
              <advanced-agent-knowledge
                v-if="workspace.advancedKnowledgeBase"
                :json-knowledge="workspace.jsonKnowledge"
                @jsonKnowledgeChanged="
                  (data) => {
                    workspace.jsonKnowledge = data;
                    updateWorkspaceInfo();
                  }
                "
              />
            </div>
          </div>

          <div class="section" v-if="canEdit()">
            <div class="line"></div>
            <div class="content">
              <div class="inline">
                <div class="inline">
                  <div class="inlineItem_near">
                    <v-btn
                      style=""
                      icon
                      :loading="setDefaultLoading"
                      @click.stop="setAsDefaultAgent"
                      :title="isDefaultAgent ? 'This is the default agent' : 'Set as default agent'"
                    >
                      <v-icon big :color="isDefaultAgent ? 'orange' : 'grey'">
                        {{ isDefaultAgent ? 'mdi-star' : 'mdi-star-outline' }}
                      </v-icon>
                    </v-btn>
                    <span>{{ isDefaultAgent ? 'Default Agent' : 'Set as Default' }}</span>
                  </div>
                  <div class="inlineItem_near">
                    <v-btn
                      style=""
                      icon
                      @click.stop="
                        () => {
                          workspace.private = !workspace.private;
                          updateWorkspaceInfo();
                        }
                      "
                    >
                      <v-icon big>
                        {{
                          workspace.private
                            ? 'mdi-lock'
                            : 'mdi-lock-open-variant-outline'
                        }}
                      </v-icon>
                    </v-btn>
                    <span>{{ workspace.private ? 'Private' : 'Public' }}</span>
                  </div>
                  <div class="inlineItem_near">
                    <v-btn
                      style=""
                      icon
                      @click.stop="isParticipantDialogOpen = true"
                    >
                      <v-icon big> mdi-account-supervisor-circle </v-icon>
                    </v-btn>
                    <span> Participants </span>
                  </div>
                  <div class="inlineItem_near">
                    <v-btn
                      style=""
                      icon
                      @click.stop="isCommunicatioChannelsDialog = true"
                    >
                      <v-icon big> mdi-chat </v-icon>
                    </v-btn>
                    <span> Communication Channels </span>
                  </div>
                  <div class="inlineItem_near">
                    <v-btn
                      style=""
                      icon
                      @click.stop="isSkillEditorDialog = true"
                    >
                      <v-icon big> mdi-cog </v-icon>
                    </v-btn>
                    <span> Skills </span>
                  </div>
                </div>
                <div class="inlineItem_end">
                  <v-icon
                    small
                    v-if="
                      !(updateWorkspaceLoading || updateSystemPromptLoading)
                    "
                  >
                    mdi-check
                  </v-icon>
                  <v-progress-circular
                    v-if="updateWorkspaceLoading || updateSystemPromptLoading"
                    :size="10"
                    :width="1"
                    indeterminate
                  />
                  <span
                    style="margin-left: 5px; font-size: 10pt; color: #4a4a4a"
                    >{{
                      updateWorkspaceLoading || updateSystemPromptLoading
                        ? 'Saving...'
                        : 'Saved to Agent'
                    }}</span
                  >
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div style="display: flex; flex-direction: column; padding: 45px">
        <div style="flex-grow: 0">
          <v-row class="ChatStart__input">
            <v-col>
              <v-card
                outlined
                class="rounded-xxl"
                style="padding: 5px; width: 100%"
                @dragover.prevent
                @drop.prevent="onFileDrop"
              >
                <v-textarea
                  v-model="message"
                  class="ChatStart__textarea"
                  no-resize
                  hide-details
                  rows="3"
                  id="message_input2"
                  placeholder="Type your message here..."
                  @keydown.enter.exact.prevent="send"
                  @keydown.enter.ctrl.exact.prevent="() => {}"
                  @keydown.enter.shift.exact.prevent="() => {}"
                ></v-textarea>

                <v-card-actions class="pa-2">
                  <v-file-input
                    v-model="files"
                    hide-input
                    prepend-icon="mdi-paperclip"
                    class="ChatStart__file-input"
                    multiple
                    accept=".doc,.docx,.pdf,.csv,.txt,.html,.odt,.rtf,.epub,.jpeg,.png,.jpg,image/*"
                    @change="onFileChange"
                  ></v-file-input>

                  <v-spacer></v-spacer>

                  <v-btn
                    v-if="(message?.trim()?.replaceAll('\n', '') || '') !== ''"
                    @click.stop="saveWorkspacePrompt"
                    :loading="workspaceActionLoading"
                    text
                    color="primary"
                    class="mr-2"
                  >
                    Save Prompt
                  </v-btn>

                  <v-btn
                    color="primary"
                    icon
                    @click.stop="send"
                    :loading="sendMessageLoading"
                  >
                    <v-icon>mdi-send</v-icon>
                  </v-btn>
                </v-card-actions>
              </v-card>

              <workspace-prompt
                class="mt-2"
                @onPromptSelect="
                  (data) => {
                    this.message = data.prompt;
                  }
                "
              />

              <v-row
                v-if="
                  filesAccumulated.length !== 0 || linkPreviews.length !== 0
                "
                class="Chat__files"
              >
                <v-card outlined class="rounded-lg" width="100%">
                  <v-card-text class="pa-0">
                    <div class="filesContainer">
                      <div
                        v-for="(file, index) in filesAccumulated"
                        :key="index"
                        class="fileItem"
                      >
                        <v-card outlined>
                          <v-card-text class="pa-2 d-flex align-center">
                            <v-btn
                              icon
                              x-small
                              class="mr-2"
                              @click.stop="onFileDelete(index)"
                            >
                              <v-icon small>mdi-close</v-icon>
                            </v-btn>

                            <v-avatar
                              v-if="file.type.includes('image')"
                              size="32"
                              class="mr-2"
                            >
                              <v-img :src="createObjectURL(file)" />
                            </v-avatar>

                            <v-avatar
                              v-else
                              size="32"
                              color="primary"
                              class="mr-2"
                            >
                              <v-icon small color="white">mdi-file</v-icon>
                            </v-avatar>

                            <span class="text-body-2 text-truncate">{{
                              file.name
                            }}</span>

                            <v-btn
                              :title="
                                filesMetadata[index].isPatientList
                                  ? 'Unmark'
                                  : 'Mark as patient list'
                              "
                              icon
                              small
                              class="mr-2"
                              @click.stop="
                                updatePatientListStatus(index, 'file')
                              "
                            >
                              <v-icon>{{
                                !filesMetadata[index].isPatientList
                                  ? 'mdi-account-multiple-remove'
                                  : 'mdi-account-multiple-check'
                              }}</v-icon>
                            </v-btn>
                          </v-card-text>
                        </v-card>
                      </div>

                      <div
                        v-for="(linkData, index) in linkPreviews"
                        :key="index"
                        class="fileItem"
                        @click.stop="openLink(linkData.url)"
                      >
                        <v-card outlined>
                          <v-card-text class="pa-2 d-flex align-center">
                            <v-btn
                              icon
                              x-small
                              class="mr-2"
                              @click.stop="onLinkDelete(index)"
                            >
                              <v-icon small>mdi-close</v-icon>
                            </v-btn>

                            <v-avatar
                              size="32"
                              class="mr-2"
                              v-if="linkData?.favicons?.length > 0"
                            >
                              <v-img
                                :src="
                                  linkData?.favicons?.length > 0
                                    ? linkData?.favicons[0]
                                    : ''
                                "
                              />
                            </v-avatar>

                            <div>
                              <div>
                                <span
                                  class="text-body-2 text-truncate"
                                  style="max-width: 250px; display: block"
                                  >{{ linkData?.title || linkData.url }}</span
                                >
                              </div>
                              <div>
                                <span
                                  class="text-body-2 text-truncate"
                                  style="width: 250px; display: block"
                                  >{{ linkData?.description }}</span
                                >
                              </div>
                            </div>
                            <v-btn
                              :title="
                                linkAttachments[index]?.isPatientList
                                  ? 'Unmark'
                                  : 'Mark as patient list'
                              "
                              icon
                              small
                              class="mr-2"
                              @click.stop="
                                updatePatientListStatus(index, 'link')
                              "
                            >
                              <v-icon>{{
                                !linkAttachments[index]?.isPatientList
                                  ? 'mdi-account-multiple-remove'
                                  : 'mdi-account-multiple-check'
                              }}</v-icon>
                            </v-btn>
                          </v-card-text>
                        </v-card>
                      </div>
                    </div>
                  </v-card-text>
                </v-card>
              </v-row>
            </v-col>
          </v-row>
        </div>
      </div>

      <v-row
        class="Workspace__sessions d-flex flex-column"
        v-if="artifactList?.length !== 0"
      >
        <div class="titleWrapper mb-4">
          <span class="title"> Your artifacts </span>
        </div>

        <artifacts-list
          :artifacts-list="artifactList"
          :loading="artifactsLoading"
          :show-load-more="false"
        />
      </v-row>

      <v-row class="Workspace__sessions d-flex flex-column">
        <div class="titleWrapper mb-4">
          <span class="title"> Your sessions </span>
        </div>

        <chat-session-list
          :sessions="sessions"
          :loading="sessionsLoading"
          :show-load-more="false"
        />
      </v-row>

      <tools-select
        :dialog="isToolSelectDialog"
        :toolSets="workspace.tools"
        @close="isToolSelectDialog = false"
        @toolsChanged="
          (data) => {
            workspace.tools = data;
            updateWorkspaceInfo();
          }
        "
      />

      <workspace-participant
        :dialog="isParticipantDialogOpen"
        @close="isParticipantDialogOpen = false"
      />

      <agent-communication-channels
        :dialog="isCommunicatioChannelsDialog"
        @close="isCommunicatioChannelsDialog = false"
      />

      <skill-editor
        :dialog="isSkillEditorDialog"
        @close="isSkillEditorDialog = false"
      />
    </v-col>

    <v-col v-if="workspaceLoading">
      <Loader></Loader>
    </v-col>
  </div>
</template>

<script>
import Loader from '@/components/loader/loader.vue';
import { availableAIModels, defaultModel } from '@/util/availableAIModels';
import constants from '@/util/constants';
import ToolsSelect from '@/views/ai-workspaces/create-workspace/tools-select/tools-select.vue';
import ArtifactsList from '@/views/ai-workspaces/workspace/artifacts/artifact-list.vue';
import AgentCommunicationChannels from '@/views/ai-workspaces/workspace/communication-channels/communication-channels.vue';
import GeneralActions from '@/views/ai-workspaces/workspace/general-actions/general-actions.vue';
import AdvancedAgentKnowledge from '@/views/ai-workspaces/workspace/knowledge/advanced-knowledge/advanced-knowledge.vue';
import WorkspaceKnowledge from '@/views/ai-workspaces/workspace/knowledge/workspace-knowledge.vue';
import WorkspaceParticipant from '@/views/ai-workspaces/workspace/participants/participants.vue';
import SkillEditor from '@/views/ai-workspaces/workspace/skill-editor/skill-editor.vue';
import WorkspacePrompt from '@/views/ai-workspaces/workspace/workspace-prompts/workspace-prompts.vue';
import debounce from 'lodash/debounce';
import lodashGet from 'lodash/get';
import * as moment from 'moment';
import { createNamespacedHelpers } from 'vuex';
import ChatSessionList from '../../ai-chat/components/chat-session-list.vue';

const { mapGetters: customerGetters } = createNamespacedHelpers('customer');
const {
  mapGetters: AIChatGetters,
  mapActions: AIChatActions,
  mapMutations: AIChatMutations,
} = createNamespacedHelpers('AIChat');
const {
  mapGetters: workspacesGetters,
  mapActions: workspaceActions,
  mapMutations: workspacesMutations,
} = createNamespacedHelpers('aiWorkspace');

const { mapActions: sharedActions } = createNamespacedHelpers('shared');

export default {
  name: 'AgentDetails',
  components: {
    ArtifactsList,
    AgentCommunicationChannels,
    AdvancedAgentKnowledge,
    GeneralActions,
    WorkspacePrompt,
    WorkspaceKnowledge,
    WorkspaceParticipant,
    ToolsSelect,
    Loader,
    ChatSessionList,
    SkillEditor,
  },
  data() {
    return {
      isWorkspaceKnowledgeOpen: false,
      memory: {
        now: true,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
      message: '',
      publicPath: constants.isDevelopment ? '' : '',
      includeAllHistory: false,
      isToolSelectDialog: false,
      isParticipantDialogOpen: false,
      isCommunicatioChannelsDialog: false,
      isSkillEditorDialog: false,
      historyLimit: 15,
      models: availableAIModels,
      selectedModel: defaultModel.value,
      files: [],
      filesAccumulated: [],
      filesMetadata: [],
      selectedSystemPrompt: null,
      systemPromptText: '',
      setDefaultLoading: false,

      linkPreviews: [],
      linkAttachments: [],
    };
  },
  computed: {
    ...customerGetters({
      customer: 'CUSTOMER',
      selectedCustomer: 'SELECTED_CUSTOMER',
      user: 'USER',
    }),
    ...AIChatGetters({
      systemPrompts: 'SYSTEM_PROMPTS',
      systemPromptLoading: 'SYSTEM_PROMPT_LOADING',
      updateSystemPromptLoading: 'UPDATE_SYSTEM_PROMPT_LOADING',
      sendMessageLoading: 'SEND_MESSAGE_LOADING',
      sessions: 'SESSIONS_LIST',
      sessionsLoading: 'SESSIONS_LIST_LOADING',
    }),
    ...workspacesGetters({
      workspace: 'WORKSPACE',
      updateWorkspaceLoading: 'WORKSPACE_UPDATE_LOADING',
      workspaceLoading: 'WORKSPACE_LOADING',
      workspaceActionLoading: 'IS_WORKSPACE_PROMPTS_ACTION_LOADING',
      selectedPrompt: 'SELECTED_WORKSPACE_PROMPT',
      workspacePrompts: 'WORKSPACE_PROMPTS',
      artifactList: 'GET_ARTIFACT_LIST',
      artifactsLoading: 'GET_IS_ARTIFACT_LIST_LOADING',
    }),
    isExtendedModeSupported() {
      return (
        this.selectedModel === 'us.anthropic.claude-3-7-sonnet-20250219-v1:0' ||
        this.selectedModel === 'us.anthropic.claude-sonnet-4-20250514-v1:0' ||
        this.selectedModel ===
          'global.anthropic.claude-sonnet-4-5-20250929-v1:0' ||
        this.selectedModel === 'us.anthropic.claude-opus-4-6-v1' ||
        this.selectedModel === 'us.anthropic.claude-sonnet-4-6'
      );
    },
    maxTokens: {
      get() {
        return this.workspace.maxTokens;
      },
      set(value) {
        const newMax = Math.max(1000, Math.min(value, 15000));
        if (newMax < (this.workspace.thinkingTokens || 0)) {
          this.setWorkspace({
            ...this.workspace,
            maxTokens: newMax + 1,
            thinkingTokens: newMax,
          });
        } else {
          this.setWorkspace({
            ...this.workspace,
            maxTokens: newMax,
          });
        }
      },
    },
    thinkingTokens: {
      get() {
        return this.workspace.thinkingTokens;
      },
      set(value) {
        if (value > (this.workspace.maxTokens || 1000)) {
          this.setWorkspace({
            ...this.workspace,
            maxTokens: value + 1,
            thinkingTokens: value,
          });
        } else {
          this.setWorkspace({
            ...this.workspace,
            thinkingTokens: value,
          });
        }
      },
    },
    isDefaultAgent() {
      return this.customer?.CustomersFeature?.default_workspace_id === this.workspace?.id;
    },
  },
  watch: {
    'selectedCustomer.customer_id'() {
      //this.getInfo();
    },
    systemPromptText(newVal) {
      this.debounceSystemPrompt(newVal);
    },
  },
  methods: {
    ...sharedActions(['getLinkPreview']),
    ...AIChatActions([
      'sendMessage',
      'getToolSets',
      'getSystemPrompts',
      'updateSystemPrompt',
      'createSystemPrompt',
      'getWorkspaceSessions',
    ]),
    ...AIChatMutations({
      setSendMessageLoading: 'SET_SEND_MESSAGE_LOADING',
      setSendMessage: 'SET_SEND_MESSAGE',
      setChatWorkspace: 'SET_CHAT_WORKSPACE',
    }),
    ...workspacesMutations({
      pushToWorkspacePrompt: 'PUSH_TO_WORKSPACE_PROMPTS',
      setWorkspacePrompts: 'SET_WORKSPACE_PROMPTS',
      setWorkspaceLoading: 'SET_WORKSPACE_LOADING',
      setWorkspace: 'SET_WORKSPACE',
    }),
    ...workspaceActions([
      'getWorkspace',
      'updateWorkspace',
      'createWorkspacePrompt',
      'updateWorkspacePrompt',
      'getWorkspaceArtifacts',
      'setDefaultAgent',
    ]),
    debounceSystemPrompt: debounce(function (e) {
      this.systemPromptText = e;
      this.saveSystemPrompt();
    }, 600),
    updatePatientListStatus(index, type) {
      if (type === 'link') {
        this.linkAttachments[index].isPatientList =
          !this.linkAttachments[index]?.isPatientList;
      }
      if (type === 'file') {
        this.filesMetadata[index].isPatientList =
          !this.filesMetadata[index]?.isPatientList;
      }
    },
    openLink(url) {
      window.open(url, '_blank');
    },
    async saveWorkspacePrompt() {
      if (this.selectedPrompt?.id) {
        const prompt = await this.updateWorkspacePrompt({
          prompt: this.message,
          workspaceId: this.workspace.id,
          workspacePromptId: this.selectedPrompt?.id,
        });
        const prompts = [...this.workspacePrompts];
        const index = prompts.findIndex((item) => item?.id === prompt?.id);
        prompts[index] = prompt;
        this.setWorkspacePrompts([...prompts]);
        return;
      }
      const prompt = await this.createWorkspacePrompt({
        prompt: this.message,
        workspaceId: this.workspace.id,
      });
      this.pushToWorkspacePrompt(prompt);
    },
    createObjectURL(file) {
      return URL.createObjectURL(file);
    },
    isShAdmin() {
      return lodashGet(this.customer, 'customer_id') === 0;
    },
    toBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
      });
    },
    async saveSystemPrompt() {
      if (this.selectedSystemPrompt?.id) {
        await this.updateSystemPrompt({
          systemPromptId: this.selectedSystemPrompt.id,
          prompt: this.systemPromptText,
        });
        return;
      }
      const prompt = await this.createSystemPrompt({
        prompt: this.systemPromptText,
        name: this.systemPromptText,
      });
      this.systemPrompts.push(prompt);
      this.selectedSystemPrompt = prompt;
      this.systemPromptText = prompt.prompt;
    },
    async updateSelectedSystemPrompt(value) {
      const systemPrompt = this.systemPrompts.find(
        (prompt) => prompt.id === value,
      );
      this.systemPromptText = systemPrompt.prompt;
      this.selectedSystemPrompt = systemPrompt;
    },
    getTime(date) {
      return moment(date).fromNow();
    },
    onFileDelete(index) {
      this.filesAccumulated.splice(index, 1);
      this.filesMetadata.splice(index, 1);
      this.files = [];
    },
    onLinkDelete(index) {
      this.linkAttachments.splice(index, 1);
      this.linkPreviews.splice(index, 1);
    },
    onFileChange(event) {
      event.forEach((thisFile) => {
        if (this.validateSize(thisFile)) {
          this.$store.dispatch(
            'notifications/setError',
            `${thisFile.name} too big`,
          );
          return;
        }
        this.filesAccumulated.push(thisFile);
        this.filesMetadata.push({ isPatientList: false });
      });
      this.files = [];
    },
    async prepareFiles() {
      const prepared = await Promise.all(
        this.filesAccumulated.map(async (file) => {
          const base64 = await this.toBase64(file);
          return {
            base64: base64,
            name: file.name,
            type: file.type,
          };
        }),
      );
      return prepared;
    },
    validateSize(input) {
      const fileSize = input.size / 1024 / 1024; // in MiB
      return fileSize > 1;
    },
    debounceInput: debounce(function (e) {
      this.search = e;
    }, 1000),
    async getInfo(workspaceId) {
      this.setSendMessageLoading(false);
      await this.getWorkspace({ workspaceId });
      this.selectedSystemPrompt = this.workspace?.SystemPrompt;
      this.systemPromptText = this.selectedSystemPrompt?.prompt;
      this.selectedModel = this.models.find(
        (model) => model.value === this.workspace.model,
      )?.value;
      this.historyLimit = this.workspace.historyLimit;
      this.includeAllHistory =
        this.workspace.historyLimit === null ||
        this.workspace.historyLimit === undefined;
      this.setChatWorkspace(this.workspace);
      await this.getSystemPrompts();
      this.setWorkspaceLoading(false);
      await this.getWorkspaceSessions({ workspaceId });
      await this.getWorkspaceArtifacts({ workspaceId });
    },
    async updateWorkspaceInfo() {
      if (this.workspace?.id && !this.workspaceLoading) {
        const historyLimit = this.historyLimit !== '' ? this.historyLimit : 0;
        const maxTokens =
          this.workspace.maxTokens !== '' ? this.workspace.maxTokens : 1025;
        const thinkingTokens =
          this.workspace.thinkingTokens !== ''
            ? this.workspace.thinkingTokens
            : 1024;
        await this.updateWorkspace({
          workspaceId: this.workspace.id,
          toolSetIds: this.workspace.tools.map((tool) => tool.id),
          systemPromptId: this.selectedSystemPrompt?.id,
          model: this.selectedModel,
          name: this.workspace.name,
          maxTokens: maxTokens,
          historyLimit: this.includeAllHistory ? null : historyLimit,
          private: this.workspace.private,
          jsonKnowledge: this.workspace.jsonKnowledge,
          advancedKnowledgeBase: this.workspace.advancedKnowledgeBase,
          modelThinking: this.workspace.modelThinking,
          thinkingTokens: thinkingTokens || maxTokens,
        });
      }
    },

    async send() {
      if (this.message === '') {
        return;
      }
      const messageValue = this.message.valueOf();
      this.message = '';
      this.setSendMessageLoading(true);

      this.setChatWorkspace(this.workspace);
      this.setSendMessage({
        workspaceId: this.workspace.id,
        workspace: this.workspace,
        files: this.filesAccumulated,
        filesMetadata: this.filesMetadata,
        linkAttachments: this.linkAttachments,
        messageValue,
        memory: this.memory,
      });

      this.$router.push({ path: `/ai-chat` });
    },

    goToSession(id) {
      this.$router.push({ path: `/ai-chat`, query: { session: id } });
    },

    canEdit() {
      return this?.user?.is_privileged;
    },
    async setAsDefaultAgent() {
      if (this.isDefaultAgent) {
        return; // Already default, no action needed
      }

      this.setDefaultLoading = true;
      try {
        await this.setDefaultAgent({ workspaceId: this.workspace.id });
      } catch (error) {
        // Error handling is already done in the store action
      } finally {
        this.setDefaultLoading = false;
      }
    },
    onFileDrop(e) {
      if (!e.dataTransfer || !e.dataTransfer.files) return;
      const droppedFiles = Array.from(e.dataTransfer.files);
      if (droppedFiles.length === 0) return;
      this.onFileChange(droppedFiles);
    },
  },
  async beforeDestroy() {
    this.setWorkspaceLoading(true);
  },
  async mounted() {
    const { workspaceId } = this.$route.params;
    await this.getInfo(workspaceId);
    document
      .getElementById('message_input2')
      .addEventListener('keydown', async (e) => {
        const getCaret = (el) => {
          if (el.selectionStart) {
            return el.selectionStart;
          } else if (document.selection) {
            el.focus();
            const r = document.selection.createRange();
            if (r == null) {
              return 0;
            }
            const re = el.createTextRange(),
              rc = re.duplicate();
            re.moveToBookmark(r.getBookmark());
            rc.setEndPoint('EndToStart', re);
            return rc.text.length;
          }
          return 0;
        };

        const start = e.target.selectionStart;
        const end = e.target.selectionEnd;
        const caret = getCaret(e.target);
        if (
          e.key == 'Enter' &&
          e.ctrlKey == false &&
          e.shiftKey == false &&
          this.message.replaceAll('\n', '') !== ''
        ) {
          await this.send();
        }
        if (e.key == 'Enter' && e.ctrlKey == true) {
          this.message =
            this.message.substring(0, caret) +
            '\n' +
            this.message.substring(end);
          e.stopPropagation();
          setTimeout(() => {
            e.target.selectionStart = e.target.selectionEnd = start + 1;
          }, 1);
        }
        if (e.key == 'Enter' && e.shiftKey == true) {
          this.message =
            this.message.substring(0, caret) +
            '\n' +
            this.message.substring(end);
          e.stopPropagation();
          setTimeout(() => {
            e.target.selectionStart = e.target.selectionEnd = start + 1;
          }, 1);
        }
      });

    document.getElementById('message_input2').onpaste = async (event) => {
      const items = (
        event?.clipboardData || event?.originalEvent?.clipboardData
      ).items;
      for (const index in items) {
        const item = items[index];
        if (item.kind === 'file') {
          const blob = item.getAsFile();
          this.filesAccumulated ??= [];
          this.filesAccumulated.push(blob);
          continue;
        }

        let pastedText = '';
        if (window.clipboardData && window.clipboardData.getData) {
          // IE
          pastedText = window.clipboardData.getData('Text');
        } else if (event.clipboardData && event.clipboardData.getData) {
          pastedText = event.clipboardData.getData('text/plain');
        }
        // eslint-disable-next-line
        const linkRegex = /^https?:\/\/(?:www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(?:[-a-zA-Z0-9()@:%_\+.~#?&\/=]*)$/
        if (linkRegex.test(pastedText)) {
          const preview = await this.getLinkPreview(pastedText);
          this.linkPreviews.push(preview);
          this.linkAttachments.push({
            url: pastedText,
            isPatientList: false,
          });
        }
      }
    };
  },
};
</script>

<style lang="scss" scoped>
@import './workspace';
</style>
